import React from 'react';
import { Folder, Database, Loader2, Search, BookOpen } from 'lucide-react';
import { Folder as FolderType, DataSource, FolderContextOut } from '../../types';
import { apiService } from '../../services/api';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { toast } from 'react-hot-toast';
import ConnectorLogo, { hasConnectorLogo } from '../ConnectorLogo';

interface AddContextModalProps {
  isOpen: boolean;
  onClose: () => void;
  folderId: string;
  folderName: string;
  existingContext: FolderContextOut[];
  onAdded?: () => void;
}

type Tab = 'folders' | 'data_sources' | 'knowledge_bases';

const CONNECTOR_LABELS: Record<string, string> = {
  google_drive: 'Google Drive',
  google_search_console: 'Google Search Console',
};

function getConnectorLabel(connectorType: string): string {
  return CONNECTOR_LABELS[connectorType] || 'Other';
}

const AddContextModal: React.FC<AddContextModalProps> = ({
  isOpen,
  onClose,
  folderId,
  folderName,
  existingContext,
  onAdded,
}) => {
  const [activeTab, setActiveTab] = React.useState<Tab>('folders');
  const [folders, setFolders] = React.useState<FolderType[]>([]);
  const [dataSources, setDataSources] = React.useState<DataSource[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [adding, setAdding] = React.useState(false);
  const [folderSearch, setFolderSearch] = React.useState('');
  const [dsSearch, setDsSearch] = React.useState('');
  const [dsFilter, setDsFilter] = React.useState<string>('all');
  const [selectedFolderIds, setSelectedFolderIds] = React.useState<Set<string>>(new Set());
  const [selectedDsIds, setSelectedDsIds] = React.useState<Set<string>>(new Set());
  const [kbFolders, setKbFolders] = React.useState<FolderType[]>([]);
  const [selectedKbFolderIds, setSelectedKbFolderIds] = React.useState<Set<string>>(new Set());
  const [selectedKbDsIds, setSelectedKbDsIds] = React.useState<Set<string>>(new Set());
  const [kbSearch, setKbSearch] = React.useState('');

  const existingFolderIds = React.useMemo(
    () => new Set(existingContext.filter((c) => c.context_type === 'folder').map((c) => c.context_folder_id!).filter(Boolean)),
    [existingContext]
  );
  const existingDsIds = React.useMemo(
    () => new Set(existingContext.filter((c) => c.context_type === 'data_source').map((c) => c.context_data_source_id!).filter(Boolean)),
    [existingContext]
  );

  const dataSourceFolderIds = React.useMemo(
    () => new Set((dataSources || []).map((ds) => ds.folder_id).filter(Boolean)),
    [dataSources]
  );

  React.useEffect(() => {
    if (!isOpen) return;
    setFolderSearch('');
    setDsSearch('');
    setDsFilter('all');
    setKbSearch('');
    setLoading(true);
    Promise.all([
      apiService.getFolders(true, 'user'),
      apiService.getDataSources(),
      apiService.getFolders(true, 'knowledge_base'),
    ])
      .then(([foldersList, dsRes, kbList]) => {
        setFolders(foldersList);
        setDataSources(dsRes.data_sources || []);
        setKbFolders(kbList || []);
      })
      .catch((err) => {
        toast.error(err?.message || 'Failed to load folders and data sources');
      })
      .finally(() => setLoading(false));
  }, [isOpen]);

  const toggleFolder = (id: string) => {
    if (id === folderId || existingFolderIds.has(id)) return;
    setSelectedFolderIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleDataSource = (id: string) => {
    if (existingDsIds.has(id)) return;
    setSelectedDsIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleKbFolder = (id: string) => {
    if (existingFolderIds.has(id)) return;
    setSelectedKbFolderIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleKbDataSource = (id: string) => {
    if (existingDsIds.has(id)) return;
    setSelectedKbDsIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAddSelected = async () => {
    const toAdd: Array<{ type: 'folder' | 'data_source'; id: string }> = [];
    if (activeTab === 'knowledge_bases') {
      selectedKbFolderIds.forEach((id) => toAdd.push({ type: 'folder', id }));
      selectedKbDsIds.forEach((id) => toAdd.push({ type: 'data_source', id }));
    } else {
      selectedFolderIds.forEach((id) => toAdd.push({ type: 'folder', id }));
      selectedDsIds.forEach((id) => toAdd.push({ type: 'data_source', id }));
    }
    if (toAdd.length === 0) {
      toast.error('Select at least one folder or data source');
      return;
    }
    setAdding(true);
    let added = 0;
    let failed = 0;
    for (const { type, id } of toAdd) {
      try {
        if (type === 'folder') {
          await apiService.addFolderContext(folderId, { context_type: 'folder', context_folder_id: id });
        } else {
          await apiService.addFolderContext(folderId, { context_type: 'data_source', context_data_source_id: id });
        }
        added++;
      } catch (err: any) {
        failed++;
        const msg = err?.response?.data?.detail || err?.message || 'Failed to add';
        toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
      }
    }
    setAdding(false);
    if (added > 0) {
      toast.success(`Added ${added} context${added > 1 ? 's' : ''}`);
      setSelectedFolderIds(new Set());
      setSelectedDsIds(new Set());
      setSelectedKbFolderIds(new Set());
      setSelectedKbDsIds(new Set());
      onAdded?.();
      onClose();
    }
  };

  const availableFolders = React.useMemo(
    () =>
      folders.filter(
        (f) =>
          f.id !== folderId &&
          !existingFolderIds.has(f.id) &&
          !dataSourceFolderIds.has(f.id)
      ),
    [folders, folderId, existingFolderIds, dataSourceFolderIds]
  );

  const filteredFolders = React.useMemo(() => {
    const q = folderSearch.trim().toLowerCase();
    if (!q) return availableFolders;
    return availableFolders.filter((f) => f.name.toLowerCase().includes(q));
  }, [availableFolders, folderSearch]);

  const dataSourcesByConnector = React.useMemo(() => {
    const map: Record<string, DataSource[]> = {};
    dataSources
      .filter((ds) => !existingDsIds.has(ds.id))
      .forEach((ds) => {
        const key = ds.connector_type || 'other';
        if (!map[key]) map[key] = [];
        map[key].push(ds);
      });
    return map;
  }, [dataSources, existingDsIds]);

  const connectorTypes = React.useMemo(
    () => Array.from(new Set(dataSources.map((ds) => ds.connector_type || 'other'))).sort(),
    [dataSources]
  );

  const filteredDataSourcesByConnector = React.useMemo(() => {
    if (dsFilter === 'all') return dataSourcesByConnector;
    const out: Record<string, DataSource[]> = {};
    if (dataSourcesByConnector[dsFilter]?.length) {
      out[dsFilter] = dataSourcesByConnector[dsFilter];
    }
    return out;
  }, [dataSourcesByConnector, dsFilter]);

  const kbFolderIds = React.useMemo(() => new Set(kbFolders.map((f) => f.id)), [kbFolders]);
  const kbDataSources = React.useMemo(
    () => (dataSources || []).filter((ds) => ds.folder_id && kbFolderIds.has(ds.folder_id)),
    [dataSources, kbFolderIds]
  );
  const availableKbFolders = React.useMemo(
    () => kbFolders.filter((f) => f.id !== folderId && !existingFolderIds.has(f.id)),
    [kbFolders, folderId, existingFolderIds]
  );
  const availableKbDataSources = React.useMemo(
    () => kbDataSources.filter((ds) => !existingDsIds.has(ds.id)),
    [kbDataSources, existingDsIds]
  );
  const kbSearchLower = kbSearch.trim().toLowerCase();
  const filteredKbFolders = React.useMemo(() => {
    if (!kbSearchLower) return availableKbFolders;
    return availableKbFolders.filter((f) => f.name.toLowerCase().includes(kbSearchLower));
  }, [availableKbFolders, kbSearchLower]);
  const filteredKbDataSources = React.useMemo(() => {
    if (!kbSearchLower) return availableKbDataSources;
    return availableKbDataSources.filter(
      (ds) =>
        (ds.display_name && ds.display_name.toLowerCase().includes(kbSearchLower)) ||
        (ds.connector_type && ds.connector_type.toLowerCase().includes(kbSearchLower))
    );
  }, [availableKbDataSources, kbSearchLower]);

  const dataSourcesSearchLower = dsSearch.trim().toLowerCase();
  const filteredDataSourcesByConnectorWithSearch = React.useMemo(() => {
    if (!dataSourcesSearchLower) return filteredDataSourcesByConnector;
    const out: Record<string, DataSource[]> = {};
    Object.entries(filteredDataSourcesByConnector).forEach(([connectorType, list]) => {
      const label = getConnectorLabel(connectorType).toLowerCase();
      const filtered = list.filter(
        (ds) =>
          (ds.display_name && ds.display_name.toLowerCase().includes(dataSourcesSearchLower)) ||
          label.includes(dataSourcesSearchLower) ||
          (ds.connector_type && ds.connector_type.toLowerCase().includes(dataSourcesSearchLower))
      );
      if (filtered.length > 0) out[connectorType] = filtered;
    });
    return out;
  }, [filteredDataSourcesByConnector, dataSourcesSearchLower]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Add context to "${folderName}"`} maxWidth="lg">
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Add folders or data sources as context. Documents from these sources will be included when chatting in this folder.
        </p>

        <div className="flex border-b border-gray-200">
          <button
            type="button"
            onClick={() => setActiveTab('folders')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              activeTab === 'folders'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Folders
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('data_sources')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              activeTab === 'data_sources'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Data sources
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('knowledge_bases')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-1.5 ${
              activeTab === 'knowledge_bases'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <BookOpen className="h-4 w-4" />
            Knowledge bases
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        ) : (
          <>
            {activeTab === 'folders' && (
              <div className="space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search folders..."
                    value={folderSearch}
                    onChange={(e) => setFolderSearch(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-md">
                  {availableFolders.length === 0 ? (
                    <p className="p-4 text-sm text-gray-500">
                      No other folders to add. Data source folders are hidden; create regular folders first.
                    </p>
                  ) : filteredFolders.length === 0 ? (
                    <p className="p-4 text-sm text-gray-500">No folders match &quot;{folderSearch}&quot;</p>
                  ) : (
                    <ul className="divide-y divide-gray-100">
                      {filteredFolders.map((f) => (
                        <li key={f.id}>
                          <label className="flex items-center gap-2 p-3 hover:bg-gray-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedFolderIds.has(f.id)}
                              onChange={() => toggleFolder(f.id)}
                              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <Folder className="h-4 w-4 text-gray-500 flex-shrink-0" />
                            <span className="text-sm text-gray-800 truncate">{f.name}</span>
                            {f.document_count != null && f.document_count > 0 && (
                              <span className="text-xs text-gray-400">({f.document_count} docs)</span>
                            )}
                          </label>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
            {activeTab === 'knowledge_bases' && (
              <div className="space-y-3">
                <p className="text-sm text-gray-500">
                  Add Knowledge Base folders or their data sources as context. They won&apos;t appear in your folder tree but will be used when chatting here.
                </p>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search knowledge bases..."
                    value={kbSearch}
                    onChange={(e) => setKbSearch(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-md space-y-4">
                  {filteredKbFolders.length === 0 && filteredKbDataSources.length === 0 ? (
                    <p className="p-4 text-sm text-gray-500">
                      No knowledge bases to add, or all are already added.
                      {kbSearch.trim() && ' Try a different search.'}
                    </p>
                  ) : (
                    <>
                      {filteredKbFolders.length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 px-2 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide sticky top-0 bg-amber-50 rounded">
                            <BookOpen className="h-4 w-4 flex-shrink-0" />
                            Knowledge Base folders
                          </div>
                          <ul className="divide-y divide-gray-100">
                            {filteredKbFolders.map((f) => (
                              <li key={f.id}>
                                <label className="flex items-center gap-2 p-3 hover:bg-gray-50 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={selectedKbFolderIds.has(f.id)}
                                    onChange={() => toggleKbFolder(f.id)}
                                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                  />
                                  <BookOpen className="h-4 w-4 text-amber-600 flex-shrink-0" />
                                  <span className="text-sm text-gray-800 truncate">{f.name}</span>
                                  {f.document_count != null && f.document_count > 0 && (
                                    <span className="text-xs text-gray-400">({f.document_count} docs)</span>
                                  )}
                                </label>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {filteredKbDataSources.length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 px-2 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide sticky top-0 bg-gray-50 rounded">
                            <Database className="h-4 w-4 flex-shrink-0" />
                            Data sources in Knowledge Bases
                          </div>
                          <ul className="divide-y divide-gray-100">
                            {filteredKbDataSources.map((ds) => (
                              <li key={ds.id}>
                                <label className="flex items-center gap-2 p-3 hover:bg-gray-50 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={selectedKbDsIds.has(ds.id)}
                                    onChange={() => toggleKbDataSource(ds.id)}
                                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                  />
                                  {hasConnectorLogo(ds.connector_type || '') ? (
                                    <ConnectorLogo connectorType={ds.connector_type!} size={16} className="flex-shrink-0" alt="" />
                                  ) : (
                                    <Database className="h-4 w-4 text-gray-500 flex-shrink-0" />
                                  )}
                                  <span className="text-sm text-gray-800 truncate">
                                    {ds.display_name || (ds.connector_type || '').replace(/_/g, ' ')}
                                  </span>
                                </label>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
            {activeTab === 'data_sources' && (
              <div className="space-y-3">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search data sources..."
                      value={dsSearch}
                      onChange={(e) => setDsSearch(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <select
                    value={dsFilter}
                    onChange={(e) => setDsFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white min-w-[120px]"
                  >
                    <option value="all">All types</option>
                    {connectorTypes.map((ct) => (
                      <option key={ct} value={ct}>
                        {getConnectorLabel(ct)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-md space-y-4 p-2">
                  {Object.keys(filteredDataSourcesByConnectorWithSearch).length === 0 ? (
                    <p className="p-4 text-sm text-gray-500">
                      No data sources to add, or all are already added.
                      {dsSearch.trim() && ' Try a different search.'}
                      {dsFilter !== 'all' && !dsSearch.trim() && ' Try "All types".'}
                    </p>
                  ) : (
                    Object.entries(filteredDataSourcesByConnectorWithSearch).map(([connectorType, list]) => (
                      <div key={connectorType}>
                        <div className="flex items-center gap-2 px-2 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide sticky top-0 bg-gray-50 rounded">
                          {hasConnectorLogo(connectorType) ? (
                            <ConnectorLogo connectorType={connectorType} size={14} className="flex-shrink-0" alt="" />
                          ) : (
                            <Database className="h-4 w-4 flex-shrink-0" />
                          )}
                          {getConnectorLabel(connectorType)}
                        </div>
                        <ul className="divide-y divide-gray-100">
                          {list.map((ds) => (
                            <li key={ds.id}>
                              <label className="flex items-center gap-2 p-3 hover:bg-gray-50 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={selectedDsIds.has(ds.id)}
                                  onChange={() => toggleDataSource(ds.id)}
                                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                />
                                {hasConnectorLogo(connectorType) ? (
                                  <ConnectorLogo connectorType={connectorType} size={16} className="flex-shrink-0" alt="" />
                                ) : (
                                  <Database className="h-4 w-4 text-gray-500 flex-shrink-0" />
                                )}
                                <span className="text-sm text-gray-800 truncate">
                                  {ds.display_name || connectorType.replace(/_/g, ' ')}
                                </span>
                              </label>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleAddSelected}
            disabled={
              loading ||
              adding ||
              (activeTab === 'knowledge_bases'
                ? selectedKbFolderIds.size === 0 && selectedKbDsIds.size === 0
                : selectedFolderIds.size === 0 && selectedDsIds.size === 0)
            }
          >
            {adding ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Adding...
              </>
            ) : (
              `Add ${
                activeTab === 'knowledge_bases'
                  ? selectedKbFolderIds.size + selectedKbDsIds.size
                  : selectedFolderIds.size + selectedDsIds.size
              } selected`
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AddContextModal;
